import React, { useEffect, useRef, useState } from 'react';
import { ProductForm, getUrlWithVariant } from '@shopify/theme-product-form';
import SizeSelector from './SizeSelector';
import { cartMutation } from '@hooks';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  selectedVariantState,
  productMeasurements,
  measurementsState,
  baseUnitState,
  calculationSpecs,
  shippingTableState,
  freightTableState,
} from '@state';
import { useMount, useUpdateEffect } from 'ahooks';
import { formatMoney } from '@shopify/theme-currency';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { getApplicationTags, getMeasurementInUnit } from '@utils';
import { useQuery } from 'react-query';
import { getVariantFromId } from '@shopify/theme-product';
import VariantSelector from './VariantSelector';
import PremiumInfo from './PremiumInfo';
import StockNotificationForm from './StockNotificationForm';
import { getProductName } from '@state/calculations';

const shouldAddGiftsMeta = (variant, payload) => {
  switch (variant.sku) {
    case 'AD-DrawerDivider-1':
      payload = [
        {
          ...payload[0],
        },
        {
          quantity: 1,
          id: 42831683256558,
        },
      ];
      // payload = [
      //   {
      //     ...payload[0],
      //     properties: { ...payload.properties, Gift: 'Acrylic Drawer Divider Pad', _Gift_SKU: 'AD-DrawerDivider-1-2"PAD' },
      //   },
      // ];
      break;
    case 'AD-DrawerDivider-2':
      payload = [
        ...payload,
        {
          quantity: 1,
          id: 42831683289326,
        },
      ];
      break;
    case 'AD-DrawerDivider-3':
      payload = [
        ...payload,
        {
          quantity: 1,
          id: 42831683322094,
        },
      ];
      break;

    default:
      break;
  }

  return payload;
};

const ProductFormEl = ({ product, ctx }) => {
  const [loading, setLoading] = useState(false);
  const [productMeta, setProductMeta] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [configurableProduct, setConfigurableProduct] = useState(false);
  const [atcButtonEnabled, setAtcButtonEnabled] = useState(false);

  const setShippingTable = useSetRecoilState(shippingTableState);
  const setFreight = useSetRecoilState(freightTableState);
  const measurements = useRecoilValue(productMeasurements);
  const baseUnit = useRecoilValue(baseUnitState);
  const specs = useRecoilValue(calculationSpecs);
  const roomName = useRecoilValue(measurementsState('roomName'));
  const [selectedVariant, setSelectedVariant] = useRecoilState(selectedVariantState);
  const addToCart = cartMutation();
  const formRef = useRef();
  const formController = useRef();
  const [mainProdApplication, setMainProdApplication] = useState();

  useMount(() => {
    let tags = getApplicationTags(product.tags, 'application');
    if (tags) {
      setMainProdApplication(tags[0]);
    }
    let productMeta = document.getElementById('metafields')?.innerText;
    try {
      setProductMeta(JSON.parse(productMeta));
    } catch (error) {
      console.error(error);
    }
  });

  useEffect(() => {
    if (formRef.current) {
      formController.current = new ProductForm(formRef.current, product, {
        onOptionChange: handleOptionChange,
      });
    }
  }, [formRef.current]);

  /**
   * Load product configuration from URL parameters
   */
  useMount(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const variantID = urlParams.get('variant');
    if (variantID) {
      let preloadVariant = getVariantFromId(product, Number(variantID));
      setSelectedVariant(preloadVariant);
    } else {
      if (product.variants.length === 1) {
        setSelectedVariant(product.variants[0]);
      }
    }
  });

  const { data: productStyleSpec, isSuccess: hasProductType } = useQuery(
    ['configurableProd', product.id],
    async () => {
      try {
        const response = await axios(`${process.env.PROXY_BASE_PATH}/public/style-specs/${product.id}`);
        return response.data;
      } catch (error) {
        console.error(error);
      }
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      enabled: Boolean(product),
    }
  );

  // console.log(productStyleSpec);

  const { data: shippingPackages } = useQuery(
    ['shippingPackageTables'],
    async () => {
      try {
        const response = await axios(`${process.env.PROXY_BASE_PATH}/public/packages`);
        setShippingTable(response.data);
        return response.data;
      } catch (error) {
        console.error(error);
      }
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: Boolean(productStyleSpec),
    }
  );

  const { data: freightData } = useQuery(
    ['freightCosts'],
    async () => {
      try {
        const response = await axios(`${process.env.PROXY_BASE_PATH}/public/freight`);
        setFreight(response.data);
        return response.data;
      } catch (error) {
        console.error(error);
      }
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: Boolean(shippingPackages),
    }
  );

  const {
    data: productVendor,
    isFetched: fetchedVendor,
    isFetching: fetchingVendor,
  } = useQuery(
    ['productVendor', product.id],
    async () => {
      return await fetch(`${process.env.PROXY_BASE_PATH}/public/product/${product.id}/vendor`)
        .then((r) => r.json())
        .then((result) => {
          return result;
        });
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: Boolean(freightData),
    }
  );

  /**
   * Determine whether or not this is a custom product
   */
  useUpdateEffect(() => {
    if (productStyleSpec && productStyleSpec.data !== null) {
      setConfigurableProduct(true);
      ctx.emit('product_schematics_loaded');
    }
  }, [productStyleSpec]);

  useUpdateEffect(() => {
    if (mainProdApplication === 'Acrylic Tray') {
      setAtcButtonEnabled(true);
    }
  }, [mainProdApplication]);

  const generateProduct = async (bundleId) => {
    let yotpoGroupTag = product.tags.find((tag) => tag.includes('YGroup_'));

    const response = await axios.post(`${process.env.PROXY_BASE_PATH}/public/product`, {
      product: {
        title: getProductName(productStyleSpec, measurements.width),
        tags: ['BTO', 'algolia-ignore', yotpoGroupTag],
        variants: [
          {
            option1: 'Config',
            price: measurements.configCost / 100,
          },
        ],
        metafields: [
          {
            key: 'hidden',
            value: 1,
            type: 'integer',
            namespace: 'seo',
          },
        ],
        images: [{ src: `https:${product.featured_image}` }],
      },
    });
    return response.data?.body.product;
  };
  
  const handleOptionChange = (ev) => {
    let variant = formController.current.variant();
    if (variant) {
      setSelectedVariant(variant);
      // Update variant URL
      const url = getUrlWithVariant(window.location.href, variant.id);
      window.history.replaceState({ path: url }, '', url);
    }
  };

  const handleFormSubmit = async (ev, prod) => {
    ev.preventDefault();
    setLoading(true);
    // When product has a BundleID assigned it will be included on PO email notifications
    let bundleID = uuidv4();

    if ((configurableProduct && atcButtonEnabled) || product.type.includes('Custom')) {
      let product = await generateProduct(bundleID);
      addToCart
        .mutateAsync({
          items: [
            {
              quantity: 1,
              id: product.variants[0].id,
              properties: {
                Width: getMeasurementInUnit(measurements.width, baseUnit, 'metric'),
                Length: getMeasurementInUnit(measurements.length, baseUnit, 'metric'),
                Height: getMeasurementInUnit(measurements.height, baseUnit, 'metric'),
                _userUnit: baseUnit,
                _Bundle: bundleID,
                _baseProductHandle: prod.handle,
                _baseProductId: prod.id,
                _orientation: '0deg',
                _featuredImage: prod.featured_image,
                _factoryCost: measurements.factoryCost,
              },
            },
          ],
        })
        .then((response) => {
          setLoading(false);
          ctx.emit('cart.update', { cart: response.data });
        })
        .catch((error) => {
          setLoading(false);
        });
    } else {
      let simple_product_payload = [
        {
          quantity: quantity,
          id: selectedVariant.id,
        },
      ];
      // check if product is a subscription
      if (product.selling_plan_groups && product.selling_plan_groups.length > 0) {
        // console.log('handleFormSubmit', product);
        simple_product_payload[0].selling_plan = product.selling_plan_groups[0].selling_plans[0].id;
      }

      // Format payload to include gifts metadata
      let payload = shouldAddGiftsMeta(selectedVariant, simple_product_payload);
      addToCart
        .mutateAsync({
          items: payload,
        })
        .then((response) => {
          setLoading(false);
          ctx.emit('cart.update', { cart: response.data });
        })
        .catch((error) => {
          setLoading(false);
          console.error(error);
        });
    }
  };

  if (!product) {
    return null;
  }

  let comingSoonTag = product.tags.find((item) => item.indexOf('Coming Soon') > -1);
  let discountTag = product.tags.find((item) => item.indexOf('Discount:') > -1);
  var discountType = 'percent';
  if (discountTag?.includes('$')) {
    discountType = 'absolute';
  }

  let statusTag = product.tags.find((item) => item.indexOf('Status:') > -1);

  if (product && fetchedVendor && configurableProduct && !Boolean(Object.keys(productVendor)?.length)) {
    if (comingSoonTag) {
      return <div className="Product__availabilityLabel">{comingSoonTag}</div>;
    } else {
      return <div className="Product__availabilityLabel">Product currently unavailable.</div>;
    }
  }

  if (comingSoonTag) {
    return <div className="Product__availabilityLabel">{comingSoonTag}</div>;
  }

  const addToCartLabel = () => {
    if (!product.available) {
      return 'Out of Stock';
    }
    if (Boolean(selectedVariant) && !selectedVariant.available) {
      return 'Out of Stock';
    }
    if (product.price_varies && !Boolean(selectedVariant)) {
      return 'Select options above';
    } else {
      return (
        <>
          {productMeta?.preorder ? <span>Pre-Order</span> : <span>Add to Cart</span>}
          {Boolean(measurements?.configCost) && (
            <>
              - <strong>{formatMoney(measurements.configCost)}</strong>
            </>
          )}
        </>
      );
    }
  };

  return (
    <>
      <form ref={formRef} action="/cart/add" id={`product_form_${product.id}`} onSubmit={(ev) => handleFormSubmit(ev, product)}>
        {specs && measurements && (
          <>
            {measurements.minCost && specs && (
              <div className="Product__priceRange">
                <span className="min">
                  <small>Starting at</small> {formatMoney(measurements.minCost)}
                </span>
                <PremiumInfo></PremiumInfo>
              </div>
            )}
            {productMeta?.short_description && (
              <div className="Product__shortDescription" dangerouslySetInnerHTML={{ __html: productMeta.short_description }}></div>
            )}
            <SizeSelector product={product} ctx={ctx}></SizeSelector>
          </>
        )}
        {!configurableProduct && (
          <div className="Product__priceRange">
            {selectedVariant ? (
              <>
                {selectedVariant.compare_at_price && <span className="compareAt">{formatMoney(selectedVariant.compare_at_price)}</span>}
                <span>{formatMoney(selectedVariant.price)}</span>
              </>
            ) : (
              <>
                {product.price_varies ? (
                  <>
                    <span>{formatMoney(product.price_min)}</span> - <span>{formatMoney(product.price_max)}</span>
                  </>
                ) : (
                  <span>{formatMoney(product.price)}</span>
                )}
              </>
            )}
            {discountTag && (
              <div className="Product__discountInfo">
                <div>
                  -{discountTag.split(':')[1]}
                  {discountType == 'percent' && '%'} off at checkout.
                </div>
              </div>
            )}
            {statusTag && (
              <div className="Product__statusInfo">
                <div>{statusTag.split(':')[1]}</div>
              </div>
            )}
          </div>
        )}
        {product.variants.length > 1 && (
          <>
            <VariantSelector product={product} handleVariantSelection={setSelectedVariant}></VariantSelector>
          </>
        )}

        <button
          type="submit"
          className="button"
          data-style="solid darkgray"
          data-layout="block"
          disabled={
            addToCart.isLoading ||
            (configurableProduct && !hasProductType) ||
            (!configurableProduct && !selectedVariant?.available) ||
            (configurableProduct && !Boolean(measurements?.configCost)) ||
            !product.available
          }
        >
          {hasProductType ? <>{loading ? 'Adding...' : addToCartLabel()}</> : <span>Loading Product</span>}
        </button>
        {!selectedVariant?.available && <StockNotificationForm variant={selectedVariant} />}
        {productMeta?.preorder && (
          <div className="preOrder-notice">
            <strong>Pre-order</strong>: {productMeta.preorder}
          </div>
        )}
      </form>
    </>
  );
};

export default ProductFormEl;
